<template>
    <PageContentLoader v-if="!ring" />

    <PageErrorFiveHundred
        v-else-if="error"
        :errors="responseErrors"
    >
        <ThButton
            :loading="fetching"
            @click="executeReadyToGoQuery"
        >
            {{ t('globals.try_again') }}
        </ThButton>
    </PageErrorFiveHundred>

    <div v-else-if="ring">
        <ProductBreadcrumbs :breadcrumbs />

        <div
            :class="{ 'fetching': fetching }"
            class="product-wrapper"
        >
            <ProductGallery :product="ring" />

            <div class="product-details">
                <div
                    v-qa="'product-information'"
                    class="product-information"
                >
                    <h1
                        v-qa="'ring-name'"
                        class="product-title"
                    >
                        <span class="name">
                            {{ ring.productName }}
                        </span>
                    </h1>

                    <p
                        v-qa="'ring-description'"
                        class="description"
                    >
                        {{ ring.translation.description }}
                    </p>

                    <TaxSnippet :data="ring.salePrice" />

                    <FinanceOptions
                        :affirm="ring.financeOptionsAffirm"
                        :klarna="ring.financeOptionsKlarna"
                    />
                </div>

                <div
                    v-if="
                        weightOptions.length ||
                        gemTypeOptions.length ||
                        !ring.isReadyToShip
                    "
                    class="product-customization"
                >
                    <ThSelect
                        v-if="weightOptions.length"
                        v-model="weightIndex"
                        v-qa="'weight'"
                        :legend="'Diamond weight'"
                        :options="weightOptions"
                        class="weight"
                    />

                    <ThSelect
                        v-if="gemTypeOptions.length"
                        v-model="gemTypeIndex"
                        v-qa="'gem-type'"
                        :legend="'Gem type'"
                        :options="gemTypeOptions"
                        class="gem"
                    />

                    <ThSelect
                        v-if="!ring.isReadyToShip"
                        v-model="ringSizeId"
                        v-qa="'size'"
                        :error="ringSizeError"
                        :legend="'Ring size'"
                        :options="ringSizeOptions"
                        class="size"
                    />
                </div>

                <DeliveryTimeframe
                    v-if="deliveryDate"
                    :data="deliveryDate"
                />

                <AddToCartFloatingButton>
                    <template #button>
                        <ThButton
                            :full-width="true"
                            :loading="fetchingExistingCustomer"
                            type="solid"
                            @click="handleAddToCart"
                        >
                            Add to cart
                        </ThButton>
                    </template>
                </AddToCartFloatingButton>

                <PdpAccordion
                    :items="
                        responseContent.data.value?.pageProductDetails
                            ?.accordionItems ?? []
                    "
                    :resizeable="false"
                    about-this-entity-label="ring"
                    :product="ring"
                    :related-products="{
                        products: ring.relatedProducts,
                    }"
                />

                <AuthFormInModal
                    :is-open="isEnquiryModalOpen"
                    :registration-disabled="fetchingNewCustomer"
                    :registration-errors="errors"
                    :content
                    @close="closeEnquiryModal"
                    @register="submitNewCustomer"
                    @login="handleAddToCart"
                >
                </AuthFormInModal>
            </div>
        </div>

        <CmsBlocks
            :data="dataContent?.pageProductDetails?.blocks"
            :product="ring"
            :related-products="{
                products: ring.relatedProducts,
            }"
            page-type="product-details"
        />
        <Reviews :data="ring.reviews" />
    </div>

    <PageErrorFourHundredFour v-else />
</template>

<script lang="ts" setup>
import {
    computed,
    defineAsyncComponent,
    hydrateOnIdle,
    hydrateOnVisible,
    toRefs,
    watch,
} from 'vue'
import { useRedirect } from 'wucdbm-vite-vue-ssr'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { type MetalCode } from '@/graphql/api'
import { useReadyToGoRingBreadcrumbs } from './useReadyToGoRingBreadcrumbs'
import { useProductPageMeta } from '@/views/product/composables/useProductPageMeta'
import { useReadyToGoRingProperties } from './useReadyToGoRingProperties'
import { useReadyToGoAddToCart } from './useReadyToGoAddToCart'
import { useResponseError } from '@/helpers/errors'
import { useProductAnalytics } from '../composables/analytics/useProductAnalytics'
import { useRoutes } from '@/router/routes'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useRegionPlugin } from '@/plugins/region.ts'
import { useTranslator } from '@/i18n'
import { useDeliveryDate } from '@/views/product/composables/useDeliveryDate.ts'
import {
    useGetReadyToGoRingContentQuery,
    useGetReadyToGoRingQuery,
} from '@/views/product/ready-to-go-ring/ReadyToGoRing.generated.ts'

import ProductBreadcrumbs from '@/components/rings/ProductBreadcrumbs.vue'
import ProductGallery from '@/components/layout/gallery/ProductGallery.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import ThSelect from '@/components/ui/inputs/ThSelect.vue'
import DeliveryTimeframe from '@/components/rings/DeliveryTimeframe.vue'
import TaxSnippet from '@/components/rings/TaxSnippet.vue'
import FinanceOptions from '@/components/rings/FinanceOptions.vue'
import PageContentLoader from '@/components/ui/PageContentLoader.vue'
import PdpAccordion from '@/components/product/PdpAccordion.vue'
import AuthFormInModal from '@/components/user/auth/AuthFormInModal.vue'
import { useInit } from '@/plugins/init/use-init.ts'
import AddToCartFloatingButton from '../components/AddToCartFloatingButton.vue'

const PageErrorFiveHundred = defineAsyncComponent({
    loader: () => import('@/components/PageErrorFiveHundred.vue'),
    hydrate: hydrateOnVisible(),
})

const PageErrorFourHundredFour = defineAsyncComponent({
    loader: () => import('@/components/PageErrorFourHundredFour.vue'),
    hydrate: hydrateOnVisible(),
})

const CmsBlocks = defineAsyncComponent({
    loader: () => import('@/components/cms/CmsBlocks.vue'),
    hydrate: hydrateOnIdle(),
})

const Reviews = defineAsyncComponent({
    loader: () => import('@/components/rings/Reviews.vue'),
    hydrate: hydrateOnVisible(),
})

const props = defineProps<{
    slug: string
    metal: MetalCode
}>()

const { slug, metal } = toRefs(props)

const { t } = useI18n()
const currency = useCurrencyInput()
const { regionCode } = useRegionPlugin()
const { locale } = useTranslator()
const path = useRoute().path

const response = useGetReadyToGoRingQuery({
    variables: computed(() => ({
        slug: slug.value,
        metal: metal.value,
        locale: locale.value,
        currency: currency.value,
        region: regionCode.value,
    })),
})

const responseContent = useGetReadyToGoRingContentQuery({
    variables: computed(() => ({
        locale: locale.value,
        currency: currency.value,
        region: regionCode.value,
        page: path,
        pageFallback: 'ready-to-go',
    })),
})

const deliveryDate = useDeliveryDate(
    response,
    (data) => data.catalogueReadyToGoRing?.firstAvailableDeliveryDate
)

await response
await responseContent

const {
    fetching,
    data,
    error,
    stale,
    executeQuery: executeReadyToGoQuery,
} = response

const { data: dataContent } = responseContent

const responseErrors = useResponseError(response)

const {
    ring,
    weightIndex,
    weightOptions,
    gemTypeIndex,
    gemTypeOptions,
    handleRingSizeNotSelected,
    ringSizeError,
    ringSizeId,
    ringSizeOptions,
} = useReadyToGoRingProperties(data)

const redirect = useRedirect()
const routes = useRoutes()
const router = useRouter()

watch(
    ring,
    (newRing) => {
        if (!stale.value && !fetching.value && newRing === null) {
            const href = router.resolve(routes.readyToGoRings()).href
            redirect(href, 301)
        }
    },
    { immediate: true }
)

const breadcrumbs = useReadyToGoRingBreadcrumbs(ring, metal)

useProductAnalytics(ring, fetching)

useProductPageMeta(
    computed(() => dataContent.value?.pageProductDetails?.meta),
    ring,
    fetching,
    stale
)

const {
    isEnquiryModalOpen,
    closeEnquiryModal,
    submitNewCustomer,
    fetchingNewCustomer,
    fetchingExistingCustomer,
    errors,
    handleAddToCart,
} = useReadyToGoAddToCart(ring, handleRingSizeNotSelected)

const { enquiryFormContent } = useInit()

const content = computed(
    () => enquiryFormContent.value['ready-to-go-rings-add-to-cart']
)
</script>

<style lang="scss" scoped>
.product-wrapper {
    @include product-wrapper();

    .product-details {
        @include min-width(1280) {
            margin: 0 100px;
        }

        @include min-width(1360) {
            margin: 0 120px;
        }

        @include min-width(1440) {
            margin: 0 150px;
        }

        .product-information {
            padding: 0 0 25px;
            margin: 0 0 25px;
            @include border-bottom;
        }

        .product-customization {
            margin: 0 0 25px;
            @include border-bottom;

            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            flex-wrap: wrap;

            .weight {
                width: 120px;
            }

            .gem {
                width: calc(100% - 140px);
                margin-left: 20px;
            }

            .weight,
            .gem {
                margin-bottom: 20px;
            }

            .size {
                margin-bottom: 25px;
            }
        }

        .product-cta {
            padding: 0 0 25px;
            margin: 0 0 25px;
        }
    }
}
</style>
